import { Button, Tooltip } from '@ksyos/design-system';
import { sortBy } from 'lodash';
import { useEffect } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { UnavailableService } from '@common/interfaces/patientUnavailableServices';
import type { DecisionAidPlacement } from '@frontend/components/elements/DecisionAid';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { serviceCodeButtonOrder } from '@common/customControls/ggzConsultServiceCodeSortOrder';
import { ServiceType } from '@common/enums';
import { useDecisionAidComponent } from '@frontend/utils/hooks/useDecisionAidComponent';
import type { ISelectedService, IService } from '../newProcessInterfaces';
import { translateKey } from '../../../../../translate';
import { FormParagraph } from '../../../../elements/common';
import { ProcessStartWizardButtonRow } from '../../../../elements/ProcessStartWizard/ProcessStartWizardButtonRow';

interface INewProcessServiceButtonRowProps {
    decisionAidPlacement?: DecisionAidPlacement;
    onClick: (service: ISelectedService) => Promise<void>;
    selectedEpisodeCategory: EpisodeCategory | undefined;
    selectedService: ISelectedService | undefined;
    selectedServiceType: ServiceType;
    services: IService[];
    unavailableServices: UnavailableService[];
}

export function NewProcessServiceButtonRow(props: INewProcessServiceButtonRowProps) {
    const { onClick, services, selectedService, selectedServiceType, selectedEpisodeCategory } = props;

    const { DecisionAidProps, DecisionAid, decisionAidLink, hasDecisionAid, setIsDecisionAidOpen } =
        useDecisionAidComponent({
            selectedEpisodeCategory,
            selectedServiceType,
            services: sortBy(services, (service) => serviceCodeButtonOrder.indexOf(service.code)),
            selectedStep: services.find((service) => service.id === selectedService?.serviceId)?.name,
            placement: props.decisionAidPlacement,
        });

    useEffect(() => {
        if (selectedService && DecisionAidProps.isOpen) {
            setIsDecisionAidOpen(false);
        }
    }, [selectedService]);

    return (
        <>
            {hasDecisionAid && <DecisionAid {...DecisionAidProps} />}

            <ProcessStartWizardButtonRow
                actionComponent={hasDecisionAid ? decisionAidLink : undefined}
                label={translateKey(`home.chooseStartResearch_${selectedServiceType}`)}
            >
                {services.length > 0
                    ? sortBy(services, (service) => serviceCodeButtonOrder.indexOf(service.code)).map((service) => {
                          const unavailableService = props.unavailableServices.find(
                              (item) => item.serviceId === service.id
                          );

                          return unavailableService ? (
                              <Tooltip
                                  content={translateKey(
                                      `unavailableServices.${unavailableService.serviceCode.toLowerCase()}.${
                                          unavailableService.reason
                                      }.alertAndTooltip`
                                  )}
                                  key={service.id + isWithProcedure(service)}
                                  placement="bottom"
                              >
                                  <Button
                                      iconRight="information"
                                      isDisabled={true}
                                      variant="outline"
                                  >
                                      {getServiceLabel(service.code, 'processWizard', service.name)}
                                  </Button>
                              </Tooltip>
                          ) : (
                              <Button
                                  className={`automation-processFlowService-${service.code}${
                                      isWithProcedure(service) ? '-procedure' : ''
                                  }`}
                                  color="secondary"
                                  key={service.id + isWithProcedure(service)}
                                  onClick={async () => {
                                      await onClick({
                                          serviceId: service.id,
                                          withProcedure: isWithProcedure(service),
                                          newProcessCustomIdentifier: undefined,
                                          serviceCode: service.code,
                                      });
                                  }}
                                  variant={isOutlined(service, props.selectedService) ? 'outline' : 'solid'}
                              >
                                  {getServiceLabel(service.code, 'processWizard', service.name)}
                              </Button>
                          );
                      })
                    : [<FormParagraph key="1">{translateKey('home.noServices')}</FormParagraph>]}
            </ProcessStartWizardButtonRow>
        </>
    );

    function isWithProcedure(service: IService) {
        return service.serviceType === ServiceType.examination;
    }

    function isOutlined(service: IService, selectedService1: ISelectedService | undefined) {
        if (!selectedService1) {
            return true;
        }
        if (service.id !== selectedService1.serviceId || isWithProcedure(service) !== selectedService1.withProcedure) {
            return true;
        }

        return false;
    }
}
